<script lang="ts" setup>
const { getCompanyAttributes, getMetaAttributes, getQueryAttributes, getSortAttributes } = useApiAttributes()
const { version } = useApiDoc()
const { t } = useI18n()
const api = useApi()
const localePath = useLocalePath()

const { data: response, execute, status } = useAsyncData(
  'doc:lists-fetch-companies',
  () => api.companies.fetch(),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/lists/:listId/companies',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.page'),
    example: '/lists/:listId/companies?page=1',
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.size'),
    example: '/lists/:listId/companies?size=10',
    name: 'size',
    optional: true,
    type: 'number',
  },
  {
    attributes: getQueryAttributes(),
    description: t('attributes.query'),
    endpoint: '/lists/:listId/companies',
    hasSegmentation: true,
    name: 'query',
    required: true,
    type: 'string',
    warning: t('globals.stringifyAndEncode'),
  },
  ...getSortAttributes({ endpoint: '/lists/:listId/companies' }),
]

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getCompanyAttributes({ version: version.value }),
      description: t('response.companies'),
      link: {
        label: t('globals.browseAllDatapoints'),
        to: localePath({
          name: 'api-slug',
          params: { slug: 'datapoints' },
        }),
      },
      name: 'companies',
      type: 'array',
    },
    getMetaAttributes(),
  ]
})

async function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    await execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "page": "The page to fetch (default to 1).",
      "query": "The query to filter the companies inside your list.",
      "size": "The number of companies to be returned (between 1 and 100). Default to 10."
    },
    "response": {
      "companies": "The companies that belong to your list."
    }
  },
  "es": {
    "attributes": {
      "page": "La página a recuperar (por defecto: 1).",
      "query": "La consulta para filtrar las empresas dentro de tu lista.",
      "size": "El número de empresas a recuperar (entre 1 y 100). Por defecto a 10."
    },
    "response": {
      "companies": "Las empresas que pertenezcan a tu lista."
    }
  },
  "fr": {
    "attributes": {
      "page": "La page à récupérer (par défaut 1).",
      "query": "La requête pour filtrer les entreprises dans votre liste.",
      "size": "Le nombre d'entreprises à récupérer (entre 1 et 100). Par défaut 10."
    },
    "response": {
      "companies": "Les entreprises qui appartiennent à votre liste."
    }
  }
}
</i18n>
